import { Card, CardContent, CardHeader, CardTitle } from '@alertwhale/ui'
import { BarChart3, Loader2, TrendingUp, TrendingDown } from 'lucide-react'
import { useAnalytics } from '@/hooks/useAnalytics'
import { formatNumber, cn } from '@/lib/utils'

export function AnalyticsPage() {
  const { data: analytics, isLoading, error } = useAnalytics()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading analytics...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <p className="text-destructive">Failed to load analytics</p>
          <p className="text-sm text-muted-foreground mt-2">Please try refreshing the page</p>
        </div>
      </div>
    )
  }

  const growthPositive = (analytics?.notifications.growth_percentage || 0) >= 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          <span className="text-gradient">Analytics</span>
        </h1>
        <p className="text-muted-foreground">Track your notification performance</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="glass-card rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(analytics?.notifications.total || 0)}
            </div>
            <p className={cn(
              'text-xs flex items-center gap-1 mt-1',
              growthPositive ? 'text-success' : 'text-destructive'
            )}>
              {growthPositive ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              {growthPositive ? '+' : ''}{analytics?.notifications.growth_percentage || 0}% from last month
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Delivery Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.delivery.rate || '100.0'}%</div>
            <p className="text-xs text-success mt-1">Excellent performance</p>
          </CardContent>
        </Card>

        <Card className="glass-card rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(analytics?.devices.active || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(analytics?.devices.total || 0)} total registered
            </p>
          </CardContent>
        </Card>

        <Card className="glass-card rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Live Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(analytics?.live_sessions.total || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Total sessions created</p>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card rounded-xl">
        <CardHeader>
          <CardTitle>Notifications Over Time</CardTitle>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Charts coming soon</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="glass-card rounded-xl">
          <CardHeader>
            <CardTitle>Platform Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  iOS
                </span>
                <span className="font-medium">{analytics?.devices.ios_percentage || 0}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${analytics?.devices.ios_percentage || 0}%` }}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  Android
                </span>
                <span className="font-medium">{analytics?.devices.android_percentage || 0}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${analytics?.devices.android_percentage || 0}%` }}
                />
              </div>
              <div className="pt-2 border-t border-white/10">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>iOS: {formatNumber(analytics?.devices.ios_count || 0)} devices</span>
                  <span>Android: {formatNumber(analytics?.devices.android_count || 0)} devices</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card rounded-xl">
          <CardHeader>
            <CardTitle>Delivery Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-success" />
                  Delivered
                </span>
                <span className="font-medium">{analytics?.delivery.delivered_percentage || '100.0'}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-warning" />
                  Pending
                </span>
                <span className="font-medium">{analytics?.delivery.pending_percentage || '0.0'}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-destructive" />
                  Failed
                </span>
                <span className="font-medium">{analytics?.delivery.failed_percentage || '0.0'}%</span>
              </div>
              <div className="pt-4 border-t border-white/10">
                <div className="h-3 flex rounded-full overflow-hidden bg-muted">
                  <div
                    className="bg-success transition-all duration-500"
                    style={{ width: `${analytics?.delivery.delivered_percentage || 100}%` }}
                  />
                  <div
                    className="bg-warning transition-all duration-500"
                    style={{ width: `${analytics?.delivery.pending_percentage || 0}%` }}
                  />
                  <div
                    className="bg-destructive transition-all duration-500"
                    style={{ width: `${analytics?.delivery.failed_percentage || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
